import React, { useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { CustomButton, TextField } from '../../../../v2/MUI_custom_component';
import CardHeader from '../../Common/CardHeader';
import { cardStyles } from '../../Common/constants';

import { ProfilerQuestionID } from '../../config/QuestionEnum';
import CustomChip from '../../../Components/CustomChip';
import { alphaNumericInputValidation } from '../../../Common/Validation/alphaNumeric';

const MAX_BRANDS = 5;
const MAX_BRAND_LENGTH = 30;

const BrandName = ({
  questionId = ProfilerQuestionID.BCM_BRAND_NAME,
  title = 'Which brands do you work with?',
  titleHelperText = '(Add 1 or more)',
  subtitle = 'This helps us give you more Social Security Benefits',
  placeholder = 'Enter brand name',
  submitLabel = 'Submit',
  value,
  defaultValue = [],
  onChange,
  onSubmit,
  maxBrands = MAX_BRANDS,
}) => {
  const isControlled = Array.isArray(value);
  const [internalValue, setInternalValue] = useState(defaultValue);
  const [inputValue, setInputValue] = useState('');

  const brandValues = isControlled ? value : internalValue;
  const normalizedBrands = useMemo(
    () => brandValues.map((item) => item.trim().toLowerCase()),
    [brandValues],
  );

  const updateBrands = (nextValues) => {
    if (!isControlled) {
      setInternalValue(nextValues);
    }
    onChange?.(nextValues);
  };

  const addBrand = () => {
    const trimmedValue = inputValue.trim();
    if (!trimmedValue || brandValues.length >= maxBrands) {
      return;
    }

    if (normalizedBrands.includes(trimmedValue.toLowerCase())) {
      setInputValue('');
      return;
    }

    updateBrands([...brandValues, trimmedValue]);
    setInputValue('');
  };

  const removeBrand = (brandName) => {
    updateBrands(brandValues.filter((item) => item !== brandName));
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      addBrand();
    }
  };

  const handleSubmit = () => {
    onSubmit?.({
      question: [
        { id: questionId, value: brandValues }
      ]
    });
  };

  return (
    <Box sx={cardStyles({ overflow: 'hidden' })}>
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2,}}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center' }}>
            <Box 
              component='img'
              alt='Brand Icon'
              width={64}
              height={64}
              src={'https://d2djimvzddgguw.cloudfront.net/humbee_profiler/images/shilpkar.png'}
            />
            <CardHeader
              title={title}
              titleHelperText={titleHelperText}
            />
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <TextField
              variant='standard'
              placeholder={placeholder}
              value={inputValue}
              
              onChange={(event) => setInputValue(alphaNumericInputValidation(event.target.value).slice(0, MAX_BRAND_LENGTH))}
              onKeyDown={handleKeyDown}
              inputProps={{ maxLength: MAX_BRAND_LENGTH }}
              disabled={brandValues.length >= maxBrands}
              helperText={
                brandValues.length >= maxBrands
                  ? `Maximum ${maxBrands} brands allowed`
                  : !brandValues.length
                  ? 'Eg: Dalmia Cement, Welspun Shield TMT'
                  : undefined
              }
              error={brandValues.length >= maxBrands}
            />
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, maxHeight: 120, overflowY: 'auto' }}>
            {brandValues.map((brandName) => (
              <CustomChip
                key={brandName}
                label={brandName}
                selected
                onClick={() => removeBrand(brandName)}
                onDelete={() => removeBrand(brandName)}
                sx={{ maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', wordBreak: 'break-word' } }}
              />
            ))}
          </Box>
        </Box>

        <CustomButton
          size='small'
          label={submitLabel}
          onClick={handleSubmit}
          disabled={!brandValues.length}
          sx={{ width: '100%' }}
        />
      </Box>
    </Box>
  );
};

export default BrandName;
